import React, { useEffect, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CommentSection = ({ postSlug }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/comments/${postSlug}`);
        setComments(res.data);
      } catch (e) {
        console.error('Failed to load comments', e);
      } finally {
        setLoading(false);
      }
    };
    if (postSlug) load();
  }, [postSlug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !commentText.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/comments/${postSlug}`, {
        author: name.trim(),
        content: commentText.trim(),
      });
      setComments(prev => [...prev, res.data]);
      toast.success('Comment posted!');
      setCommentText('');
      setName('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-16 pt-16 border-t border-gray-200" data-testid="comments-section">
      <div className="flex items-center space-x-3 mb-8">
        <MessageCircle size={24} />
        <h3 className="text-2xl font-light">
          Comments {comments.length > 0 && `(${comments.length})`}
        </h3>
      </div>

      <form onSubmit={handleSubmit} className="mb-12 bg-gray-50 p-8 rounded-lg" data-testid="comment-form">
        <div className="mb-4">
          <label htmlFor="comment-name" className="block text-sm font-medium mb-2">Name</label>
          <input
            id="comment-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
            disabled={isSubmitting}
            data-testid="comment-name-input"
          />
        </div>

        <div className="mb-4">
          <label htmlFor="comment-text" className="block text-sm font-medium mb-2">Comment</label>
          <textarea
            id="comment-text"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Share your thoughts..."
            rows={4}
            maxLength={2000}
            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent resize-none"
            disabled={isSubmitting}
            data-testid="comment-text-input"
          />
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="comment-submit-btn"
        >
          {isSubmitting ? 'Posting...' : 'Post Comment'}
        </button>
      </form>

      {loading ? (
        <p className="text-gray-500">Loading comments...</p>
      ) : comments.length > 0 ? (
        <div className="space-y-8" data-testid="comments-list">
          {comments.map((comment) => (
            <div key={comment.id} className="flex space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium">
                  {comment.author.charAt(0).toUpperCase()}
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <span className="font-medium">{comment.author}</span>
                  <span className="text-sm text-gray-500">
                    {new Date(comment.date).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric'
                    })}
                  </span>
                </div>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{comment.content}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <MessageCircle size={48} className="mx-auto mb-4 opacity-30" />
          <p>No comments yet. Be the first to share your thoughts!</p>
        </div>
      )}
    </div>
  );
};

export default CommentSection;
